import * as React from 'react'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateCustomResourcesSchema } from 'modules/custom_resources/hooks/customResources/useCreateCustomResourcesSchema'
import schemas from 'modules/custom_resources/store/collections/customResources/schemas'

import CustomResourcesComponent from './_Component'

type CustomResourcesComponentProps = React.ComponentProps<typeof CustomResourcesComponent>

interface Props {
  topicNames: string[]
  createTopicName: string
  canManageTables: boolean
}

export const CustomResources: React.FC<Props> = observer(
  ({ topicNames, createTopicName, canManageTables }: Props) => {
    const { loading } = useUnsafeAsyncData(() => {
      return fetchSchemas()
    }, [topicNames])

    const fetchSchemas = () =>
      schemas.fetch({
        data: {
          topic_names: topicNames,
        },
      })

    const { create } = useCreateCustomResourcesSchema()
    const { company } = useCurrentSession()

    async function createSchema(name: string, topicName?: string) {
      const { data, error } = await create({
        topic: createTopicName,
        name: name,
        usageGroupSlug: topicName,
        companyId: company.id,
        reportable: false,
        hidden: false,
      })

      if (data?.schema?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: error || i18n.t('common.save_error'),
          time: 2000,
        })
      }

      return data?.schema?.id
    }

    const reorderSchemas: CustomResourcesComponentProps['reorderSchemas'] = async (data) => {
      const schema = schemas.find({
        id: data.id,
      })

      if (!schema) return

      const { success, value } = await asyncAction(() =>
        schema.rpc('reorder', {
          new_position: data.newPosition,
        })
      )

      if (success) await fetchSchemas()

      return value
    }

    if (loading) return <Loading />

    return (
      <CustomResourcesComponent
        schemas={schemas.models}
        createSchema={createSchema}
        reorderSchemas={reorderSchemas}
        canManageTables={canManageTables}
      />
    )
  }
)
